/**
 * CPU particle simulation (plan G8): a fixed-capacity structure-of-arrays store per VFX material and the
 * generic executor for `VfxEmitterSpec` bursts (asset-lane recipes).
 *
 * - Positions are float64 world (D-011); only the render pass (particles.ts) turns them into render space.
 * - Time is sim time in seconds. Each particle remembers its birth time (`born`) and the time it was last
 *   simulated to (`t`), so a particle born at a tick's event time is advanced to the frame's interpolated
 *   render time (a muzzle flash sits on the interpolated muzzle, not a tick behind it).
 * - Live particles are dense in [0, live); a dead one is replaced by the last. At capacity the oldest
 *   (earliest born) particle is reused.
 * - Allocation-free after creation (D-014).
 */
import type { Vec3 } from '../../core/math'
import { nextFloat, type Rng } from '../../core/rng'
import type { VfxEmitterSpec } from '../../render/contracts'

const DEG = Math.PI / 180

export interface ParticleStore {
  readonly capacity: number
  /** Live particles, dense in [0, live). */
  live: number
  /** Float64 world position (D-011). */
  x: Float64Array
  y: Float64Array
  z: Float64Array
  /** World velocity, m/s. */
  vx: Float32Array
  vy: Float32Array
  vz: Float32Array
  /** Unit emission direction (aDir): streak axis, or the surface normal for impacts. */
  dx: Float32Array
  dy: Float32Array
  dz: Float32Array
  /** Seconds. */
  life: Float32Array
  /** Width in metres (aSize.x). */
  size: Float32Array
  /** Streak length in metres (aSize.y); 0 = round billboard. */
  stretch: Float32Array
  /** Linear velocity damping per second. */
  drag: Float32Array
  /** 0..1, fixed for life (aSeed). */
  seed: Float32Array
  /** Linear tint (aTint). */
  tr: Float32Array
  tg: Float32Array
  tb: Float32Array
  /**
   * 1 for a streak that stays where it was born (emitter speed 0, e.g. a muzzle flash): it extends forward
   * from its position. 0: a moving streak trails behind its position.
   */
  anchored: Uint8Array
  /** Sim time of birth, seconds. */
  born: Float64Array
  /** Sim time the particle was last simulated to. */
  t: Float64Array
}

export function createParticleStore(capacity: number): ParticleStore {
  if (!Number.isInteger(capacity) || capacity < 1) throw new Error(`Particle capacity must be a positive integer (got ${capacity})`)
  return {
    capacity,
    live: 0,
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    vx: new Float32Array(capacity),
    vy: new Float32Array(capacity),
    vz: new Float32Array(capacity),
    dx: new Float32Array(capacity),
    dy: new Float32Array(capacity),
    dz: new Float32Array(capacity),
    life: new Float32Array(capacity),
    size: new Float32Array(capacity),
    stretch: new Float32Array(capacity),
    drag: new Float32Array(capacity),
    seed: new Float32Array(capacity),
    tr: new Float32Array(capacity),
    tg: new Float32Array(capacity),
    tb: new Float32Array(capacity),
    anchored: new Uint8Array(capacity),
    born: new Float64Array(capacity),
    t: new Float64Array(capacity),
  }
}

export function clearParticles(store: ParticleStore): void {
  store.live = 0
}

/** Index of the earliest-born live particle (ties: the lowest index), or -1 when none is live. */
export function oldestParticle(store: ParticleStore): number {
  let best = -1
  let bestBorn = Infinity
  const born = store.born
  for (let i = 0; i < store.live; i++) {
    if (born[i] < bestBorn) {
      bestBorn = born[i]
      best = i
    }
  }
  return best
}

/** A slot for a new particle: the next free one, or the oldest live one when the store is full. */
export function allocParticle(store: ParticleStore): number {
  if (store.live < store.capacity) return store.live++
  return oldestParticle(store)
}

function copyParticle(s: ParticleStore, from: number, to: number): void {
  s.x[to] = s.x[from]
  s.y[to] = s.y[from]
  s.z[to] = s.z[from]
  s.vx[to] = s.vx[from]
  s.vy[to] = s.vy[from]
  s.vz[to] = s.vz[from]
  s.dx[to] = s.dx[from]
  s.dy[to] = s.dy[from]
  s.dz[to] = s.dz[from]
  s.life[to] = s.life[from]
  s.size[to] = s.size[from]
  s.stretch[to] = s.stretch[from]
  s.drag[to] = s.drag[from]
  s.seed[to] = s.seed[from]
  s.tr[to] = s.tr[from]
  s.tg[to] = s.tg[from]
  s.tb[to] = s.tb[from]
  s.anchored[to] = s.anchored[from]
  s.born[to] = s.born[from]
  s.t[to] = s.t[from]
}

/** Removes particle `i` by moving the last live particle into its slot. */
export function removeParticle(store: ParticleStore, i: number): void {
  const last = --store.live
  if (i !== last) copyParticle(store, last, i)
}

/**
 * Writes into `out` a unit direction drawn uniformly over the spherical cap of half-angle `coneDeg` around
 * (dx, dy, dz) (180 = the whole sphere, 0 = exactly the axis). A zero axis falls back to +Y.
 */
export function sampleCone(dx: number, dy: number, dz: number, coneDeg: number, rng: Rng, out: Vec3): Vec3 {
  let l = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (l < 1e-9) {
    dx = 0
    dy = 1
    dz = 0
    l = 1
  }
  const ax = dx / l
  const ay = dy / l
  const az = dz / l
  const u = nextFloat(rng)
  const v = nextFloat(rng)
  if (coneDeg <= 0) {
    out.x = ax
    out.y = ay
    out.z = az
    return out
  }
  const cosMax = Math.cos(Math.min(coneDeg, 180) * DEG)
  const cosT = 1 - u * (1 - cosMax)
  const sinT = Math.sqrt(Math.max(0, 1 - cosT * cosT))
  const phi = v * Math.PI * 2
  // Orthonormal basis (b1, b2) perpendicular to the axis, from the world axis least parallel to it.
  const useX = Math.abs(ax) < 0.9
  const hx = useX ? 1 : 0
  const hy = useX ? 0 : 1
  let b1x = -az * hy
  let b1y = az * hx
  let b1z = ax * hy - ay * hx
  const bl = Math.sqrt(b1x * b1x + b1y * b1y + b1z * b1z)
  b1x /= bl
  b1y /= bl
  b1z /= bl
  const b2x = ay * b1z - az * b1y
  const b2y = az * b1x - ax * b1z
  const b2z = ax * b1y - ay * b1x
  const c = Math.cos(phi) * sinT
  const s = Math.sin(phi) * sinT
  out.x = ax * cosT + b1x * c + b2x * s
  out.y = ay * cosT + b1y * c + b2y * s
  out.z = az * cosT + b1z * c + b2z * s
  return out
}

/** Burst size for a spec: `count` rounded stochastically, so fractional counts average out. */
export function burstCount(count: number, rng: Rng): number {
  const whole = Math.floor(count)
  const frac = count - whole
  return frac > 0 && nextFloat(rng) < frac ? whole + 1 : whole
}

const dirScratch: Vec3 = { x: 0, y: 0, z: 0 }

function lerpRange(r: readonly [number, number], u: number): number {
  return r[0] + (r[1] - r[0]) * u
}

/**
 * Emits `n` particles of `spec` at float64 world (x, y, z) around direction (dx, dy, dz), inheriting
 * `spec.inheritVelocity` of the source velocity (svx, svy, svz), born at sim time `time`. Each particle
 * draws its life, size, stretch and speed uniformly from the spec ranges and its direction uniformly
 * inside the cone. Returns the number emitted (always `n`: at capacity the oldest particles are reused).
 */
export function emitParticles(
  store: ParticleStore,
  spec: VfxEmitterSpec,
  n: number,
  x: number,
  y: number,
  z: number,
  dx: number,
  dy: number,
  dz: number,
  svx: number,
  svy: number,
  svz: number,
  time: number,
  rng: Rng,
): number {
  const inherit = spec.inheritVelocity
  const anchored = spec.speed[1] <= 0 ? 1 : 0
  for (let k = 0; k < n; k++) {
    const i = allocParticle(store)
    const d = sampleCone(dx, dy, dz, spec.coneDeg, rng, dirScratch)
    const speed = lerpRange(spec.speed, nextFloat(rng))
    store.life[i] = lerpRange(spec.life, nextFloat(rng))
    store.size[i] = lerpRange(spec.size, nextFloat(rng))
    store.stretch[i] = lerpRange(spec.stretch, nextFloat(rng))
    store.seed[i] = nextFloat(rng)
    store.x[i] = x
    store.y[i] = y
    store.z[i] = z
    store.vx[i] = d.x * speed + svx * inherit
    store.vy[i] = d.y * speed + svy * inherit
    store.vz[i] = d.z * speed + svz * inherit
    store.dx[i] = d.x
    store.dy[i] = d.y
    store.dz[i] = d.z
    store.drag[i] = spec.drag
    store.tr[i] = spec.tint[0]
    store.tg[i] = spec.tint[1]
    store.tb[i] = spec.tint[2]
    store.anchored[i] = anchored
    store.born[i] = time
    store.t[i] = time
  }
  return n
}

/**
 * Advances every live particle to sim time `now`: particles past their life are removed, the rest move
 * with exact linear drag (v(t) = v0·e^(-k·t), so the result does not depend on the frame rate).
 * A particle born after `now` (an end-of-tick event drawn at an earlier interpolated time) waits.
 */
export function stepParticles(store: ParticleStore, now: number): void {
  for (let i = 0; i < store.live; i++) {
    const dt = now - store.t[i]
    if (dt <= 0) continue
    if (now - store.born[i] >= store.life[i]) {
      removeParticle(store, i)
      i--
      continue
    }
    const k = store.drag[i]
    let move = dt
    if (k > 0) {
      const f = Math.exp(-k * dt)
      move = (1 - f) / k
      store.x[i] += store.vx[i] * move
      store.y[i] += store.vy[i] * move
      store.z[i] += store.vz[i] * move
      store.vx[i] *= f
      store.vy[i] *= f
      store.vz[i] *= f
    } else {
      store.x[i] += store.vx[i] * move
      store.y[i] += store.vy[i] * move
      store.z[i] += store.vz[i] * move
    }
    store.t[i] = now
  }
}

/** Normalized age 0..1 of particle `i` at its current simulation time. */
export function particleAge(store: ParticleStore, i: number): number {
  const life = store.life[i]
  if (life <= 0) return 1
  const a = (store.t[i] - store.born[i]) / life
  return a < 0 ? 0 : a > 1 ? 1 : a
}
